import { useNavigate } from 'react-router-dom';
import { Button, GoogleButton, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Lock, Mail } from 'lucide-react';
import { useLoginProfessional } from '@/services';
import { redirectToOAuthLogin } from '@/utils/logoutBeforeLogin';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';

const schema = z.object({
	email: z.string().max(255, 'Email inválido').email('Email inválido'),
	password: z.string().min(1, 'A senha é obrigatória'),
});

type FormData = z.infer<typeof schema>;

const ProfessionalLogin = () => {
	const navigate = useNavigate();
	const { mutateAsync } = useLoginProfessional();

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const iconCommonClass = 'w-4 h-4 text-zinc-400';

	const onSubmit = async (data: FormData) => {
		const res = await mutateAsync({
			email: data.email,
			password: data.password,
		});
		if (res?.success) navigate('/profissional');
	};

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Acesso para Profissionais | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-4">
				{/* Reduzido padding do card (p-6 md:p-10) e gap (gap-6) */}
				<div className="flex w-full max-w-[460px] flex-col gap-6 rounded-[2.5rem] bg-white p-6 shadow-sm md:p-10">
					{/* Título e Subtítulo: space-y reduzido */}
					{/* Título e Subtítulo */}
					<div className="space-y-1 text-center">
						<h1 className="text-2xl font-black tracking-tight text-zinc-900 md:text-3xl">
							Acesso para Profissionais
						</h1>
						<p className="text-xs font-medium text-zinc-400 md:text-sm">
							Insira suas credenciais para entrar na área do
							profissional Plumaa.
						</p>
					</div>

					{/* Seletor de Abas */}
					<div className="w-full">
						<div className="flex w-full justify-center gap-1 rounded-xl bg-zinc-100 p-1.5">
							<button
								type="button"
								className="w-full cursor-pointer rounded-lg border-none bg-transparent px-0 py-2 text-center text-sm font-bold text-zinc-500 focus:outline-none"
								onClick={() =>
									navigate('/profissional/cadastro')
								}
							>
								Cadastro
							</button>
							<button
								type="button"
								className="w-full cursor-pointer rounded-lg border-none bg-white px-0 py-2 text-center text-sm font-bold text-zinc-900 shadow-sm focus:outline-none"
							>
								Entrar
							</button>
						</div>
					</div>

					{/* Form: space-y reduzido para 5 */}
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-5"
					>
						<div className="space-y-4">
							{/* E-MAIL */}
							<div className="space-y-1.5">
								<label className="ml-1 text-[9px] font-black uppercase tracking-widest text-zinc-500">
									E-MAIL
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errors.email?.message}
									value={watch('email')}
									displayError
								>
									<Input
										{...register('email')}
										placeholder="nome@exemplo.com"
										className="h-10 border-zinc-200 text-sm focus:border-zinc-400" // h-12 -> h-10
									/>
								</InputWrapperAuth>
							</div>

							{/* SENHA */}
							<div className="space-y-1.5">
								<div className="flex items-center justify-between px-1">
									<label className="text-[9px] font-black uppercase tracking-widest text-zinc-500">
										SENHA
									</label>
									{/* <button
										type="button"
										onClick={() => navigate('/recuperar-senha')}
										className="text-[10px] font-bold text-zinc-400 hover:text-zinc-600 transition-colors"
									>
										Esqueceu?
									</button> */}
								</div>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										{...register('password')}
										placeholder="********"
										className="h-10 border-zinc-200 text-sm focus:border-zinc-400" // h-12 -> h-10
									/>
								</InputWrapperAuth>
							</div>
						</div>

						{/* BOTÃO ENTRAR: py-7 -> py-4 e rounded reduzido */}
						<Button
							type="submit"
							className="w-full justify-center rounded-xl border-none bg-[#1a1a1a] py-4 text-sm font-bold text-white transition-all hover:bg-zinc-800 md:text-base"
							text="Entrar"
							endIcon={<ArrowRight size={16} className="ml-1" />}
						/>
					</form>

					{/* Divisor e Google: space-y reduzido para 5 */}
					<div className="space-y-5">
						<div className="relative flex items-center">
							<div className="flex-grow border-t border-zinc-100"></div>
							<span className="mx-4 flex-shrink text-[9px] font-black uppercase tracking-widest text-zinc-300">
								ou
							</span>
							<div className="flex-grow border-t border-zinc-100"></div>
						</div>

						{/* GOOGLE BUTTON: py-6 -> py-3.5 */}
						<GoogleButton
							className="w-full max-w-none rounded-xl border-zinc-200 py-3.5 hover:bg-zinc-50"
							onClick={() => {
								const baseUrl =
									import.meta.env.VITE_BACKEND_URL.replace(
										/\/$/,
										'',
									);
								void redirectToOAuthLogin(
									`${baseUrl}/professional-private/login/oauth`,
								);
							}}
						/>
					</div>

					{/* FOOTER DO CARD: pt-4 -> pt-2 */}
					<div className="border-t border-zinc-50 pt-2 text-center">
						<p className="text-xs font-medium text-zinc-400">
							Não tem uma conta?{' '}
							<button
								onClick={() =>
									navigate('/profissional/cadastro')
								}
								className="font-black text-zinc-900 underline-offset-4 hover:underline"
							>
								Cadastre-se gratuitamente
							</button>
						</p>
					</div>
				</div>
			</main>
		</div>
	);
};

export { ProfessionalLogin };
